import { create } from 'zustand';
import { Booking, Table, TimeSlot } from '../types';
import { addHours, format, setHours, setMinutes } from 'date-fns';
import { tablesService } from '../services/firestore/tables';
import { bookingsService } from '../services/firestore/bookings';
import { categoriesService } from '../services/firestore/categories';

interface BookingStore {
  bookings: Booking[];
  tables: Table[];
  categories: string[];
  loading: boolean;
  error: string | null;
  fetchData: () => Promise<void>;
  addBooking: (booking: Omit<Booking, 'id' | 'status' | 'createdAt'>) => Promise<void>;
  cancelBooking: (id: string) => Promise<void>;
  updateBookingStatus: (id: string, status: Booking['status']) => Promise<void>;
  getAvailableTimeSlots: (date: Date, tableId: string) => Promise<TimeSlot[]>;
  addTable: (table: Omit<Table, 'id'>) => Promise<void>;
  updateTable: (id: string, table: Partial<Omit<Table, 'id'>>) => Promise<void>;
  removeTable: (id: string) => Promise<void>;
  addCategory: (category: string) => Promise<void>;
}

const generateTimeSlots = (date: Date): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  let currentTime = setMinutes(setHours(date, 10), 0); // Start at 10 AM
  const endTime = setMinutes(setHours(date, 22), 0); // End at 10 PM

  while (currentTime < endTime) {
    slots.push({
      start: format(currentTime, 'HH:mm'),
      end: format(addHours(currentTime, 1), 'HH:mm'),
      isAvailable: true,
    });
    currentTime = addHours(currentTime, 1);
  }

  return slots;
};

export const useBookingStore = create<BookingStore>((set, get) => ({
  bookings: [],
  tables: [],
  categories: [],
  loading: false,
  error: null,

  fetchData: async () => {
    set({ loading: true, error: null });
    try {
      const [tables, bookings, categories] = await Promise.all([
        tablesService.getAll(),
        bookingsService.getAll(),
        categoriesService.getAll()
      ]);
      set({ tables, bookings, categories, loading: false });
    } catch (error) {
      set({ error: 'Failed to fetch data', loading: false });
    }
  },

  addBooking: async (booking) => {
    try {
      const newBooking: Omit<Booking, 'id'> = {
        ...booking,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };
      await bookingsService.add(newBooking);
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to add booking' });
    }
  },

  cancelBooking: async (id) => {
    try {
      await bookingsService.updateStatus(id, 'cancelled');
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to cancel booking' });
    }
  },

  updateBookingStatus: async (id, status) => {
    try {
      await bookingsService.updateStatus(id, status);
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to update booking status' });
    }
  },

  getAvailableTimeSlots: async (date, tableId) => {
    const slots = generateTimeSlots(date);
    try {
      const bookings = await bookingsService.getByDateAndTable(
        format(date, 'yyyy-MM-dd'),
        tableId
      );

      return slots.map((slot) => ({
        ...slot,
        isAvailable: !bookings.some((booking) =>
          booking.timeSlots.includes(slot.start)
        ),
      }));
    } catch (error) {
      set({ error: 'Failed to get available time slots' });
      return slots;
    }
  },

  addTable: async (table) => {
    try {
      await tablesService.add(table);
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to add table' });
    }
  },

  updateTable: async (id, table) => {
    try {
      await tablesService.update(id, table);
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to update table' });
    }
  },

  removeTable: async (id) => {
    try {
      await tablesService.remove(id);
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to remove table' });
    }
  },

  addCategory: async (category) => {
    try {
      await categoriesService.add(category);
      get().fetchData();
    } catch (error) {
      set({ error: 'Failed to add category' });
    }
  },
}));